import React from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import './CollectionForm.css';
import useCollection from '../../hooks/useCollection';
import Loading from '../utilities/Loading';
import CollectionManagementRights from './management-rights/CollectionManagementRights';
import CollectionName from "./name/CollectionName";
import CollectionDescription from "./description/CollectionDescription";
import CollectionPublicity from "./publicity/CollectionPublicity";
import useCollectionValidation from '../../hooks/validation/collection/useCollectionValidation';
import CollectionsBreadCrumb from "../form/CollectionsBreadCrumb";
import CollectionMoodleCourses from "./moodle-courses/CollectionMoodleCourses";
import CollectionRecords from './records/CollectionRecords';
import CollectionBottomBar from './CollectionBottomBar';
import useCollectionModification from '../../hooks/useCollectionModification';
import useCollectionUpdate from '../../hooks/collection/useCollectionUpdate';
import { ProgressStatus, ROLE_ANONYMOUS, ROLE_USER_UNLISTED } from '../../Constants';
import useTitle from '../../hooks/useTitle';
import useCollections from '../../hooks/useCollections';
import useCollectionError from '../../hooks/useCollectionError';
import CollectionActions from "./card/CollectionActions";
import ListReloadButton from '../left/ListReloadButton';
import TopRow from '../right/TopRow';
import useCollectionTags from '../../hooks/collection/useCollectionTags';
import CardTags from '../utilities/CardTags';
import CollectionShareInformation from './CollectionShareInformation';
import CollectionCreators from './creator/CollectionCreators';
import CollectionKeywords from './keyword/CollectionKeywords';
import ContentTypes from '../form/ContentTypes';
import ScienceDropdown from '../form/ScienceDropdown';
import FacultiesDepartmentsDropdown from '../form/FacultiesDepartmentsDropdown';
import { useTranslation } from 'react-i18next';
import HelpDialog from "../dialog/HelpDialog";

const resolveVisibility = (published, contributors = []) => {
  const visibilities = [];
  if (published === 'ROLE_USER_UNLISTED') {
    visibilities.push('status_unlisted');
  } else if (published === 'ROLE_ANONYMOUS') {
    visibilities.push('status_published');
  } else {
    visibilities.push('status_private');
  }
  if (contributors.some(c => c.startsWith('grp-'))) {
    visibilities.push('status_moodle');
  }
  return visibilities;
};

const formatCreatedDate = (date, language) => {
  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(new Date(date));
};

const CollectionForm = () => {
  const [setTitle] = useTitle();
  const [originalCollection, loading, reload, httpError] = useCollection(true);
  const errorPage = useCollectionError(originalCollection, httpError);
  const [progress, update, resetProgress] = useCollectionUpdate();
  const [_collections, _loading, reloadCollections] = useCollections();
  const [isValid, messages, validate] = useCollectionValidation(['title', 'description', 'creators', 'persons', 'contentType', 'science', 'facultyDepartment']);
  const [collection, onChange, modified, undo] = useCollectionModification(originalCollection, validate, resetProgress);
  const [tags] = useCollectionTags([ { ...originalCollection, visibility: resolveVisibility(originalCollection?.published, originalCollection?.contributors) }]);
  const collectionHasRecords = collection?.eventColumns?.length > 0;
  const { t, i18n } = useTranslation();

  if (errorPage && !loading) {
    return errorPage;
  }

  if (originalCollection?.title) {
    setTitle(originalCollection.title);
  }

  const users = collection?.persons || [];

  const groups = collection?.iamgroups || [];

  const creators = collection?.creators || [];
  const publisherUserNames = creators
    .map(creator => typeof creator === 'string' ? creator : creator?.userName)
    .filter(Boolean);
  const administrators = [...new Set([...users, ...publisherUserNames])];

  const keywords = collection?.keywords?.map(kw => kw.label);

  const handleCreatorsChange = (newCreators) => {
    onChange({
      creators: newCreators,
      persons: [...new Set([...users, ...newCreators
        .map(creator => typeof creator === 'string' ? creator : creator?.userName)
        .filter(Boolean)])]
    });
  };

  const saveCollection = async (event) => {
    event.preventDefault();
    await update({
      ...collection,
      persons: [...new Set([...(collection?.persons || []), ...publisherUserNames])]
    }, modified);
    reload();
    reloadCollections();
  };

  const saveInProgress = ![
    ProgressStatus.COLLECTION_SAVE.NOT_STARTED,
    ProgressStatus.COLLECTION_SAVE.DONE
  ].includes(progress.status);

  const undoChanges = () => {
    resetProgress();
    undo();
  };

  const breadcrumb = (
    <CollectionsBreadCrumb collection={originalCollection || {}} />
  );

  return (
    <form onSubmit={saveCollection}>
      <Container className="collection-container">
          <Row className="collection-form-row">
            <Loading loading={loading}>
            <Col>
              <Container className="collection-form ps-0">
                <Row className="top-row-container">
                  <Col className="p-0">
                    <TopRow breadcrumb={breadcrumb}>
                      <CardTags tags={[ ...tags ]} />
                      <ListReloadButton onClick={reload} />
                      {!collectionHasRecords && <CollectionActions collection={collection} disabled={saveInProgress} />}
                    </TopRow>
                  </Col>
                </Row>
                <div className="collection-form-section">
                  <h2 className="collection-form-section-header">{t('collection_form_section_records')}</h2>
                  <Row className="mb-2">
                    <Col className="ps-1">
                      <CollectionRecords collection={collection} records={collection?.eventColumns || []} disabled={saveInProgress} />
                    </Col>
                  </Row>
                </div>
                <Row className="collection-created-date-share-row align-items-center justify-content-between">
                  <Col xs="auto">
                    <p className="collection-created-date mb-0">
                      {t('record_added_date')}: {formatCreatedDate(collection?.created, i18n.language)}
                    </p>
                  </Col>
                  <Col xs="auto" className="collection-created-date-share-action">
                    <CollectionShareInformation
                      identifier={collection?.identifier}
                      isShareable={[ROLE_ANONYMOUS, ROLE_USER_UNLISTED].includes(originalCollection?.published)}
                      title={collection?.title}
                    />
                  </Col>
                </Row>
                <div className="collection-form-section">
                  <h2 className="collection-form-section-header collection-form-section-header-required">
                    <span>{t('collection_form_section_basic_info')}</span>
                    <span className="collection-required-fields-note">{t('required_fields_note')}</span>
                  </h2>
                  <Row className="mb-2">
                    <Col className="ps-1">
                        <CollectionName
                            name={collection?.title}
                            onChange={(title) => onChange('title', title)}
                            message={messages.title}
                            disabled={saveInProgress}
                        />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col className="ps-1">
                        <CollectionDescription
                          description={collection?.description}
                          onChange={(description) => onChange('description', description)}
                          message={messages.description}
                          disabled={saveInProgress}
                        />
                    </Col>
                  </Row>
                </div>
                <div className="collection-form-section">
                  <h2 className="collection-form-section-header">{t('collection_form_section_publishing')}</h2>
                  <Row className="mb-2">
                    <Col className="ps-1">
                      <CollectionPublicity
                          publicity={collection?.published}
                          onChange={(publicity) => onChange('published', publicity)}
                          message={messages.publicity}
                          disabled={saveInProgress}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Row>
                      <Col className="ps-2 mb-0 collection-form-section-publisher-heading">
                        <h3>
                          {t('collection_form_section_publisher')}
                        </h3>
                        <HelpDialog label={t('collection_management_creator_help_label')} className="collection-management-creator-help-label">
                          {t('collection_management_creator_help_content')}
                        </HelpDialog>
                      </Col>
                      <Row>
                        <Col className="mb-1 collection-management-creator-subheading">
                          <div>
                            {t('collection_management_creator_subheading')}
                          </div>
                        </Col>
                      </Row>
                    </Row>
                    <Row className="collection-creators-faculties-row">
                      <Col className="collection-creators" xs={12} md={6}>
                        <CollectionCreators
                            creators={creators}
                            onChange={handleCreatorsChange}
                            isCreatorHidden={Boolean(collection?.isCreatorHidden)}
                            onVisibilityChange={(isCreatorHidden) => onChange('isCreatorHidden', isCreatorHidden)}
                            messages={messages.creators}
                        />
                      </Col>
                      <Col className="mt-1 faculties-departments" xs={12} md={6}>
                        <FacultiesDepartmentsDropdown
                            selected={collection?.facultyDepartment || ''}
                            onChange={(facultyDepartment) => onChange('facultyDepartment', facultyDepartment)}
                            disabled={saveInProgress}
                            message={messages?.facultyDepartment}
                            required={true}
                        />
                      </Col>
                    </Row>
                    <Row className="mb-2 mt-5">
                      <Col className="ps-1">
                        <CollectionMoodleCourses
                            moodleNumbers={collection?.moodleNumbers}
                            onMoodleNumberChange={(moodleNumbers) => onChange('moodleNumbers', moodleNumbers)}
                            disabled={saveInProgress}
                        />
                      </Col>
                    </Row>
                  </Row>
                </div>
                <div className="collection-form-section">
                  <Row className="mb-2">
                    <Col className="ps-1">
                      <CollectionManagementRights
                        users={administrators}
                        protectedUsers={publisherUserNames}
                        groups={groups}
                        onUserChange={(users) => onChange('persons', [...new Set([...users, ...publisherUserNames])])}
                        onGroupChange={(groups) => onChange({
                          iamgroups: groups,
                          persons: [...new Set([...(collection?.persons || []), ...publisherUserNames])]
                        })}
                        message={messages.persons}
                        disabled={saveInProgress}
                      />
                    </Col>
                  </Row>
                </div>
                <div className="collection-form-section">
                  <h2 className="collection-form-section-header">{t('categorization_form_header')}</h2>
                  <Row className="mb-2">
                    <Col className="ps-0">
                      <CollectionKeywords
                        keywords={keywords}
                        onKeywordChange={(keywords) => onChange('keywords', keywords.map(kw => ({ label: kw })))}
                        disabled={saveInProgress}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col className="ps-1">
                      <ContentTypes
                          selected={collection?.contentType || ''}
                          onChange={(contentType) => onChange('contentType', contentType)}
                          disabled={saveInProgress}
                          message={messages?.contentType}
                          required={true}
                          defaultOptionLabelKey="collection_content_type_option_default_select"
                      />
                    </Col>
                  </Row>
                </div>
                <div className="collection-form-section">
                  <Row className="mb-2">
                    <Col className="ps-1">
                      <ScienceDropdown
                        className="edit-collection-science-dropdown"
                        selected={collection?.science || ''}
                        onChange={(science) => onChange('science', science)}
                        disabled={saveInProgress}
                        message={messages?.science}
                        required={true}
                      />
                    </Col>
                  </Row>
                </div>
              </Container>
            </Col>
            </Loading>
          </Row>
        <Row className="collection-bottom-bar">
          <Col>
            <CollectionBottomBar
              progress={progress}
              modified={modified}
              disabled={saveInProgress}
              isValid={isValid}
              undo={undoChanges} />
          </Col>
        </Row>
      </Container>
    </form>
  );
};

CollectionForm.propTypes = {
};

export default CollectionForm;
